import Badge from '@orn/shared-web/components/Badge/Badge';
import { isValidDateOfBirth } from '@orn/shared-web/components/DateOfBirthInput';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import logger from '@orn/shared-web/lib/logger';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';
import * as React from 'react';

import { useDeleteResidence } from '../../../hooks/mutations/useDeleteResidence';
import { useUpdateCaregiverRelationship } from '../../../hooks/mutations/useUpdateCaregiverRelationship';
import { useUpdateDependant } from '../../../hooks/mutations/useUpdateDependant';
import { useUpdateDependantContact } from '../../../hooks/mutations/useUpdateDependantContact';
import { useUpdateDependantPartnership } from '../../../hooks/mutations/useUpdateDependantPartnership';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import { useDependantResidences } from '../../../hooks/queries/useDependantResidences';
import { useDependants } from '../../../hooks/queries/useDependants';
import useAutoCommit from '../../../hooks/useAutoCommit';
import { displayName, formatRelationship } from '../../../lib/dependants/helpers';
import { isCompleteAddress } from '../../../lib/dependants/residenceAddress';
import { getOptionalEnv } from '../../../lib/getEnv';
import { CollapsibleSettingsCard, SettingsTabLoading } from '../common';

import DependantCardBody from './DependantCardBody';
import {
  buildCardInputs,
  isCardValid,
  isMidEditFrequentPlace,
  residenceFieldErrors,
  seedCardState,
} from './dependentCardModel';
import { applyFrozenOrder, orderDependents } from './orderDependents';
import {
  buildFrequentPlaceInputs,
  buildResidenceInputs,
  hydrateFrequentLocations,
  INITIAL_FREQUENT_LOCATIONS_STATE,
  type IFrequentLocationsState,
  type IFrequentPlace,
} from './residenceForm';
import TabbedDependentsCard from './TabbedDependentsCard';

import type { IDependantCardBodyProps } from './DependantCardBody';
import type { IDependantCardState, ResidenceMode } from './dependentCardModel';
import type { IDependentRow } from './orderDependents';
import type { UpsertResidenceInput } from '../../../__generated__/globalTypes';

/** Main vs Referential for a row: the partner card is Referential only while the live "Lives with {first}" answer is yes. */
const modeForRow = (row: IDependentRow, liveTogether: boolean | null): ResidenceMode =>
  row.isPartnerCard && liveTogether === true ? 'referential' : 'main';

// ---------------------------------------------------------------------------
// Card (presentational) — one collapsible dependent card.
// ---------------------------------------------------------------------------

export interface IDependantCardProps extends IDependantCardBodyProps {
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * One collapsible dependent card: a header with the dependent's name + a
 * relationship badge, and (when expanded) the editable sections from
 * `DependantCardBody`. Fully controlled — all state + callbacks come from the
 * form wrapper.
 */
const DependantCard = ({ isExpanded, onToggle, ...body }: IDependantCardProps) => {
  const name = displayName({ firstName: body.state.firstName, preferredName: body.state.preferredName }) || 'Loved one';

  return (
    <CollapsibleSettingsCard
      isExpanded={isExpanded}
      onToggle={onToggle}
      toggleLabel={name}
      header={
        <div className="flex items-center gap-2">
          <span className="text-primary text-lg font-semibold">{name}</span>
          {body.state.relationship && (
            <Badge type="pill-color" size="sm" color="brand">
              {formatRelationship({
                relationship: body.state.relationship,
                isStep: body.state.isStep,
                isInLaw: body.state.isInLaw,
              })}
            </Badge>
          )}
        </div>
      }
    >
      <DependantCardBody {...body} />
    </CollapsibleSettingsCard>
  );
};

DependantCard.displayName = 'DependantCard';

// ---------------------------------------------------------------------------
// Base (presentational) — the dependent cards, accordion or tabbed by viewport.
// ---------------------------------------------------------------------------

export interface IDependentSettingsFormBaseProps {
  cards: IDependantCardProps[];
  saveError?: string;
}

/**
 * Presentational dependents settings form. On `lg+` viewports it renders a
 * stack of collapsible dependent cards; below `lg` (mobile/tablet) it renders
 * one `TabbedDependentsCard` with a person tab per dependent. In both layouts
 * each dependent's sections persist their own edits via `onCommit` — there is
 * no save button. Holds no data or mutations — the `DependentSettingsForm`
 * wrapper owns state, ordering, and persistence.
 */
export const DependentSettingsFormBase = ({ cards, saveError }: IDependentSettingsFormBaseProps) => {
  const isDesktop = useBreakpoint('lg');

  return (
    // [overflow-anchor:none]: see CollapsibleSettingsCard — the cards' sibling
    // (the error line) must not become a scroll anchor either.
    <div className="flex max-w-[736px] flex-col gap-5 [overflow-anchor:none]">
      {isDesktop ? (
        cards.map((card) => <DependantCard key={card.dependantId} {...card} />)
      ) : (
        <TabbedDependentsCard people={cards} />
      )}
      {saveError && <span className="text-error-primary text-sm sm:self-end">{saveError}</span>}
    </div>
  );
};

DependentSettingsFormBase.displayName = 'DependentSettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches dependents/residences/caregiver address, owns edit
// state (per-card + the shared liveTogether + frequent-locations list), and on
// each commit fans the diff out to the dependant / relationship / contact /
// residence / partnership mutations, computing residence deletes once across
// the household.
// ---------------------------------------------------------------------------

/**
 * "Loved ones" settings tab. Fetches the caregiver's dependents, their
 * residences, and the caregiver's own address and orders the cards by living
 * situation. Edits persist as they happen — each commit point (text-input
 * blur, select/checkbox/pick change) fans the per-card diff out to
 * `updateDependant`, `updateCaregiverRelationship`, `updateDependantContact`,
 * `upsertResidence`, `deleteResidence`, and `updateDependantPartnership` via
 * `Promise.allSettled`; a commit is a no-op while any card is invalid.
 * Residence deletes are computed as a single household-wide diff (hydrated ids
 * minus the ids still kept by any upsert) so a partner's or the caregiver's
 * shared residence is never deleted as a side effect.
 */
const DependentSettingsForm = () => {
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const { dependants, partnership, loading: dependantsLoading } = useDependants();
  const {
    dependantResidences,
    caregiverPrimaryResidence,
    loading: residencesLoading,
    refetch: refetchResidences,
  } = useDependantResidences();

  const [updateDependant] = useUpdateDependant();
  const [updateCaregiverRelationship] = useUpdateCaregiverRelationship();
  const [updateDependantContact] = useUpdateDependantContact();
  const [upsertResidence] = useUpsertResidence();
  const [deleteResidence] = useDeleteResidence();
  const [updateDependantPartnership] = useUpdateDependantPartnership();

  const residencesById = React.useMemo(
    () => new Map(dependantResidences.map((d) => [d.dependantId, d])),
    [dependantResidences]
  );
  const rows = React.useMemo(
    () => orderDependents(dependants, residencesById, partnership),
    [dependants, residencesById, partnership]
  );

  const [cardStates, setCardStates] = React.useState<Record<string, IDependantCardState>>({});
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [liveTogether, setLiveTogether] = React.useState<boolean | null>(null);
  const [frequentLocations, setFrequentLocations] = React.useState<IFrequentLocationsState>(
    INITIAL_FREQUENT_LOCATIONS_STATE
  );
  const baselineRef = React.useRef<Record<string, IDependantCardState>>({});
  const liveTogetherBaselineRef = React.useRef<boolean | null>(null);
  const frequentBaselineRef = React.useRef<IFrequentLocationsState>(INITIAL_FREQUENT_LOCATIONS_STATE);
  // Every hydrated, non-caregiver-owned `Residence.id`. At save we delete the
  // ones no upsert keeps (excludes `ownerKind === 'user'` so the caregiver's own
  // home is never deleted, and only deletes ids we hydrated so a partner's still
  // -kept shared row survives).
  const hydratedResidenceIdsRef = React.useRef<ReadonlySet<string>>(new Set());
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // State (not a ref) on purpose: flipping it back to true after a refetch is
  // what re-runs the seed effect — relying on the refetch's cache broadcast to
  // render is not enough, since a broadcast never comes when the refetched data
  // matches what the mutation responses already wrote.
  const [pendingHydration, setPendingHydration] = React.useState(true);
  const hasEverHydratedRef = React.useRef(false);
  // True from a residence create/delete commit until the post-refetch re-seed
  // lands — the one window where edits would be clobbered, so inputs lock.
  const [isRehydrating, setIsRehydrating] = React.useState(false);
  // Card order captured at first hydration and held for the session — saves
  // that change the ordering inputs must not shuffle the cards underfoot.
  const [frozenCardOrder, setFrozenCardOrder] = React.useState<readonly string[] | null>(null);

  // Seed editable state once the dependants are present and the residences query
  // has settled. Re-runs after a save that created/deleted residences (the save
  // re-requests hydration) so new-row ids are relinked and the delete snapshot
  // refreshes. Existing expand state is preserved across that re-hydration.
  React.useEffect(() => {
    if (!pendingHydration || dependants.length === 0 || residencesLoading) {
      return;
    }
    const seeded: Record<string, IDependantCardState> = {};
    rows.forEach((row) => {
      seeded[row.dependant.id] = seedCardState(row.dependant, residencesById.get(row.dependant.id) ?? null);
    });
    setCardStates(seeded);
    setExpanded((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (!(row.dependant.id in next)) {
          next[row.dependant.id] = false;
        }
      });
      return next;
    });
    setLiveTogether(partnership?.liveTogether ?? null);
    // Settings always shows the frequent-locations list (no yes/no gate), so the
    // toggle is forced on — that's what `buildFrequentPlaceInputs` keys off.
    setFrequentLocations({ ...hydrateFrequentLocations(dependantResidences), toggle: true });

    const hydratedIds = new Set<string>();
    for (const { primary, other } of dependantResidences) {
      if (primary !== null && primary.ownerKind !== 'user') {
        hydratedIds.add(primary.id);
      }
      for (const r of other) {
        if (r.ownerKind !== 'user') {
          hydratedIds.add(r.id);
        }
      }
    }
    hydratedResidenceIdsRef.current = hydratedIds;

    baselineRef.current = seeded;
    liveTogetherBaselineRef.current = partnership?.liveTogether ?? null;
    frequentBaselineRef.current = { ...hydrateFrequentLocations(dependantResidences), toggle: true };
    hasEverHydratedRef.current = true;
    setPendingHydration(false);
    setIsRehydrating(false);
    // First hydration only — a post-save re-hydration keeps the captured order.
    setFrozenCardOrder((prev) => prev ?? rows.map((row) => row.dependant.id));
  }, [pendingHydration, dependants, residencesLoading, rows, residencesById, dependantResidences, partnership]);

  const handleCardChange = React.useCallback(
    (id: string) => (patch: Partial<IDependantCardState>) => {
      setCardStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    },
    []
  );

  const handleToggle = React.useCallback(
    (id: string) => () => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  const handleFrequentLocationsChange = React.useCallback((places: IFrequentPlace[]) => {
    setFrequentLocations((prev) => ({ ...prev, places }));
  }, []);

  const isHydrated = hasEverHydratedRef.current;

  // Every residence that should persist after a commit (each Main card's primary
  // + the shared frequent locations), changed or not. Drives the delete diff ONLY
  // — the upserts actually fired on commit are the changed subset (see `commit`),
  // so an unchanged residence is neither re-upserted nor deleted.
  const keptResidenceInputs = React.useMemo<UpsertResidenceInput[]>(() => {
    const ups: UpsertResidenceInput[] = [];
    rows.forEach((row) => {
      const state = cardStates[row.dependant.id];
      if (state && modeForRow(row, liveTogether) === 'main') {
        const [res] = buildResidenceInputs({ pathAForm: { dependantId: row.dependant.id, form: state.residence } });
        if (res) {
          ups.push(res);
        }
      }
    });
    ups.push(...buildFrequentPlaceInputs(frequentLocations));
    return ups;
  }, [rows, cardStates, liveTogether, frequentLocations]);

  const keptResidenceIds = React.useMemo(() => {
    const kept = new Set(keptResidenceInputs.map((r) => r.residenceId).filter((id): id is string => id != null));
    // A typed-over frequent row builds no upsert, but a commit triggered
    // elsewhere (a name blur, another pick) must not sweep it while the user is
    // still mid-edit — only a replacement pick, a removal, or a cleared text
    // field lets the orphan diff delete it.
    for (const place of frequentLocations.places) {
      if (isMidEditFrequentPlace(place) && place.id !== null) {
        kept.add(place.id);
      }
    }
    return kept;
  }, [keptResidenceInputs, frequentLocations.places]);
  const orphanResidenceIds = React.useMemo(
    () => [...hydratedResidenceIdsRef.current].filter((id) => !keptResidenceIds.has(id)),
    [keptResidenceIds]
  );

  const allValid =
    rows.every((row) => {
      const state = cardStates[row.dependant.id];
      return state && isCardValid(modeForRow(row, liveTogether), state, isValidDateOfBirth);
    }) && frequentLocations.places.every((p) => p.address === null || isCompleteAddress(p.address));

  const liveTogetherChanged = liveTogether !== liveTogetherBaselineRef.current;
  const frequentChanged =
    JSON.stringify(buildFrequentPlaceInputs(frequentLocations)) !==
    JSON.stringify(buildFrequentPlaceInputs(frequentBaselineRef.current));

  const commit = async () => {
    // Mid-rehydrate the card state is about to be rebuilt from the server — a
    // diff against it could carry residence ids that no longer exist, so even a
    // coalesced re-run is intentionally discarded here. Inputs are locked for
    // that window (`isRehydrating`), so no user edit can be pending in it.
    if (pendingHydration || !allValid) {
      return;
    }

    const tasks: Promise<unknown>[] = [];
    // Fire only the residences that actually changed: each card's diffed
    // residence (from buildCardInputs) + the shared frequent list when it
    // changed. The delete diff below still uses the full keep-set, so an
    // unchanged residence is neither re-upserted nor deleted.
    const firedResidences: UpsertResidenceInput[] = [];
    for (const row of rows) {
      const state = cardStates[row.dependant.id];
      const baseline = baselineRef.current[row.dependant.id];
      if (!state || !baseline) {
        continue;
      }
      const inputs = buildCardInputs(row.dependant, modeForRow(row, liveTogether), state, baseline);
      if (inputs.dependant) {
        tasks.push(updateDependant({ variables: { input: inputs.dependant } }));
      }
      if (inputs.relationship) {
        tasks.push(updateCaregiverRelationship({ variables: { input: inputs.relationship } }));
      }
      if (inputs.contact) {
        tasks.push(updateDependantContact({ variables: { input: inputs.contact } }));
      }
      if (inputs.residence) {
        firedResidences.push(inputs.residence);
      }
    }
    if (frequentChanged) {
      firedResidences.push(...buildFrequentPlaceInputs(frequentLocations));
    }
    for (const input of firedResidences) {
      tasks.push(upsertResidence({ variables: { input } }));
    }
    for (const residenceId of orphanResidenceIds) {
      tasks.push(deleteResidence({ variables: { input: { residenceId } } }));
    }
    if (liveTogetherChanged && partnership !== null && liveTogether !== null) {
      tasks.push(
        updateDependantPartnership({
          variables: { input: { dependantPartnershipId: partnership.id, liveTogether } },
        })
      );
    }

    if (tasks.length === 0) {
      return;
    }
    setSaveError(null);

    const results = await Promise.allSettled(tasks);
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      failures.forEach((failure) => logger.error('Failed to save dependent settings', failure.reason));
      setSaveError("Couldn't save your changes. Please try again.");
      return;
    }

    // A residence create (null id → new row) or delete changes server-derived
    // references; refetch and re-hydrate so new ids relink and the delete
    // snapshot refreshes. Inputs lock for that window only — a fields-only save
    // adopts baselines in place and never disables anything. The adopted
    // baseline is this commit's snapshot, so edits made while it was in flight
    // diff as dirty and flush on the auto-commit re-run.
    const needsRefetch = orphanResidenceIds.length > 0 || firedResidences.some((r) => r.residenceId == null);
    if (needsRefetch) {
      setIsRehydrating(true);
      try {
        await refetchResidences();
      } catch (error) {
        // Mutations already landed; re-seed from whatever the cache holds
        // rather than leaving the form locked.
        logger.error('Failed to refresh residences after save', error);
      } finally {
        setPendingHydration(true);
      }
    } else {
      baselineRef.current = { ...cardStates };
      liveTogetherBaselineRef.current = liveTogether;
      frequentBaselineRef.current = frequentLocations;
    }
  };

  const requestCommit = useAutoCommit(commit);

  const handleLiveTogetherChange = React.useCallback(
    (value: boolean) => {
      setLiveTogether(value);
      // Marking the pair as living together sweeps the partner's now-contradictory
      // living situation: clear their lives-with-caregiver answer (the diff
      // persists it as `false`) — their own residence rows are already swept by
      // the household orphan-delete diff once the card is Referential.
      if (value) {
        const partner = rows.find((row) => row.isPartnerCard);
        if (partner) {
          setCardStates((prev) => {
            const state = prev[partner.dependant.id];
            if (!state || state.residence.liveWithCaregiver !== true) {
              return prev;
            }
            return {
              ...prev,
              [partner.dependant.id]: { ...state, residence: { ...state.residence, liveWithCaregiver: false } },
            };
          });
        }
      }
      requestCommit();
    },
    [rows, requestCommit]
  );

  if ((dependantsLoading || residencesLoading) && !isHydrated) {
    return <SettingsTabLoading />;
  }
  if (dependants.length === 0) {
    return <div className="text-secondary">No loved ones added yet.</div>;
  }

  // Only the rendered list is order-pinned — the commit/keep-set fan-outs above
  // iterate `rows` and are order-independent.
  const cards: IDependantCardProps[] = applyFrozenOrder(rows, frozenCardOrder)
    .filter((row) => cardStates[row.dependant.id])
    .map((row) => {
      const id = row.dependant.id;
      const state = cardStates[id];
      const emailValidation = state.email.trim() ? validateEmail(state.email) : null;
      return {
        dependantId: id,
        state,
        onChange: handleCardChange(id),
        onCommit: requestCommit,
        isExpanded: Boolean(expanded[id]),
        onToggle: handleToggle(id),
        mode: modeForRow(row, liveTogether),
        isPartnerCard: row.isPartnerCard,
        partnerName: row.partnerName,
        liveTogether,
        onLiveTogetherChange: handleLiveTogetherChange,
        frequentLocations,
        onFrequentLocationsChange: handleFrequentLocationsChange,
        caregiverResidence: caregiverPrimaryResidence,
        apiKey,
        isDisabled: isRehydrating,
        // Errors show live — with no save button, an invalid field is the only
        // visible reason edits have stopped persisting. The living-situation
        // unit (address + type cascade) saves atomically, so each missing piece
        // flags what it's holding up.
        errors: {
          firstName: !state.firstName.trim() ? 'First name is required' : undefined,
          lastName: !state.lastName.trim() ? 'Last name is required' : undefined,
          dateOfBirth:
            state.dateOfBirth !== null && !isValidDateOfBirth(state.dateOfBirth)
              ? 'Please enter a valid date of birth'
              : undefined,
          email:
            emailValidation && !emailValidation.isValid
              ? (emailValidation.error ?? 'Please enter a valid email address')
              : undefined,
          ...residenceFieldErrors(modeForRow(row, liveTogether), state.residence),
        },
      };
    });

  return <DependentSettingsFormBase cards={cards} saveError={saveError ?? undefined} />;
};

DependentSettingsForm.displayName = 'DependentSettingsForm';

export default DependentSettingsForm;
